/* eslint-disable no-nested-ternary */
import { useTranslation } from "react-i18next";
import { useState } from "react";
import AccountCard from "components/features/account/account-card/accountCard";
import {
  Tabs,
  Box,
  Button,
  //   Grid,
  Modal,
  TextInput,
  Select,
  Space,
  Group,
  rem,
  Text,
  Image,
} from "@mantine/core";
import {
  IconDownload,
  IconPrinter,
  IconPointFilled,
  IconSearch,
  IconRefresh,
  IconChevronDown,
  IconCreditCardOff,
  IconLock,
  IconFileDownload,
  IconArrowRight,
  IconAlertTriangle,
  IconArrowsUpDown,
} from "@tabler/icons-react";
import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";
import TableComponent from "components/tables/table/TableComponent";
import accountData from "modules/accounts/account-list/AccountData";
// import { ChevronDown, ChevronUp } from "lucide-react";
import { DateInput } from "@mantine/dates";
import DoughnutChart from "components/common/chart/chart";
import { useNavigate, useParams } from "react-router-dom";
import { toCurrency } from "utils/common";
import PaginationInfo from "components/tables/pagination/PaginationInfo";
import PaginationControls from "components/tables/pagination/PaginationControls";
import RoutesString from "pages/routesString";
import { useDisclosure } from "@mantine/hooks";
import { useForm } from "@mantine/form";
import { showNotification } from "@mantine/notifications";
import { FINALIZE_DEPOSIT } from "../../images";
import IconAccountImg from "@/assets/images/pngs/iconaccount.png";
import { toggleRowState } from "@/helpers/tableHelpers";
import ContractInformation from "../../account-detail/ContractInformation";

dayjs.extend(customParseFormat);

const formatTransactionDate = (value: string) => {
  if (!value) return "-";
  const parsed = dayjs(value, [
    "DD/MM/YYYY",
    "YYYY-MM-DD",
    "YYYYMMDD",
    "DD-MM-YYYY",
  ]);
  return parsed.isValid() ? parsed.format("DD/MM/YYYY") : value;
};

const { accountDetailHistory, accountRenewalHistory } = accountData;

const DepositAccountPanelDetail = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [openedRow, setOpenedRow] = useState<number | null>(null);
  const [
    isOpenContractInformation,
    { open: openContractInformation, close: closeContractInformation },
  ] = useDisclosure(false);
  const { accountId } = useParams();

  const HISTORY_PAGE_SIZE = 10;
  const [historyPage, setHistoryPage] = useState(1);
  const historyTotalPages = Math.max(
    1,
    Math.ceil(accountDetailHistory.length / HISTORY_PAGE_SIZE),
  );
  const pagedAccountHistory = accountDetailHistory.slice(
    (historyPage - 1) * HISTORY_PAGE_SIZE,
    historyPage * HISTORY_PAGE_SIZE,
  );

  const [renewalPage, setRenewalPage] = useState(1);
  const renewalTotalPages = Math.max(
    1,
    Math.ceil(accountRenewalHistory.length / HISTORY_PAGE_SIZE),
  );
  const pagedRenewalHistory = accountRenewalHistory.slice(
    (renewalPage - 1) * HISTORY_PAGE_SIZE,
    renewalPage * HISTORY_PAGE_SIZE,
  );

  const actions = [
    {
      icon: <IconCreditCardOff stroke={2} color="#2A81D0" />,
      label: t("CLOSE_ACCOUNT"),
      actionType: "CLOSE_ACCOUNT",
    },
    {
      icon: <IconLock stroke={2} color="#2A81D0" />,
      label: t("LOCK_ACCOUNT"),
      actionType: "LOCK_ACCOUNT",
    },
    {
      icon: <IconFileDownload stroke={2} color="#2A81D0" />,
      label: t("DOWNLOAD_E-DEPOSIT_CONTRACT"),
      actionType: "DOWNLOAD_E-DEPOSIT_CONTRACT",
      action: () => {
        openContractInformation();
      },
    },
    {
      icon: (
        <Box>
          <Image src={FINALIZE_DEPOSIT} w={30} />
        </Box>
      ),
      label: t("FINALIZE_DEPOSIT"),
      action: () => {
        navigate(RoutesString.FinalizeDeposit);
      },
    },
  ];

  const handleRowToggle = (index: number) => {
    setOpenedRow(toggleRowState(openedRow, index));
  };

  const pieData: any[] = [
    { label: t("PAID"), value: "40.000.000 VND", detail: 4, color: "#2A81D0" },
    {
      label: t("PRINCIPAL_AMOUNT"),
      value: "100.000.000 VND",
      detail: 10,
      color: "#AEDEF9",
    },
  ];

  const pieDepositData: any[] = [
    { label: t("SPEND"), value: `5 ${t("DAYS")}`, detail: 5, color: "#2A81D0" },
    {
      label: t("REMAINING"),
      value: `25 ${t("DAYS")}`,
      detail: 25,
      color: "#AEDEF9",
    },
  ];

  const depositData = [
    {
      value: "account summary",
      label: t("ACCOUNT_SUMMARY"),
      panelContent: (
        <Box
          style={{
            padding: "20px",
            display: "flex",
          }}
        >
          <Box
            style={{
              flex: "1",
            }}
          >
            <DoughnutChart
              title={t("DEPOSIT_TIME")}
              data={pieDepositData}
              size={230}
              thickness={30}
            />
          </Box>
          <Box
            style={{
              flex: "1",
            }}
          >
            <Box
              style={{
                borderBottom: "1px dashed #D8DDE2",
                paddingBottom: "20px",
                marginBottom: "20px",
              }}
            >
              <p className="text-[#5E656B]">{t("TOTAL_BALANCE")}:</p>
              <p className="text-[#2A81D0] text-[24px]">
                <span className="font-bold">431.209.000</span>{" "}
                <span className="text-[14px]">VND</span>
              </p>
            </Box>
            <Box
              style={{
                display: "flex",
                borderBottom: "1px dashed #D8DDE2",
                paddingBottom: "20px",
                marginBottom: "20px",
              }}
            >
              <Box
                style={{
                  marginLeft: "20px",
                  flex: 1,
                }}
              >
                <p className="text-[#5E656B]">{t("STATUS")}</p>
                <Box
                  style={{
                    display: "flex",
                    color: "#34AA44",
                  }}
                >
                  <IconPointFilled /> {t("ACTIVE")}
                </Box>
              </Box>
            </Box>
            <Box
              style={{
                display: "flex",
                borderBottom: "1px dashed #D8DDE2",
                paddingBottom: "20px",
                marginBottom: "20px",
              }}
            >
              <Box
                style={{
                  borderRight: "1px dashed #D8DDE2",
                  marginLeft: "20px",
                  flex: 1,
                }}
              >
                <p className="text-[#5E656B]">{t("PRINCIPAL_AMOUNT")}</p>
                <p className="text-[#131618] text-[16px] font-bold">
                  600.000.000 VND
                </p>
              </Box>
              <Box
                style={{
                  marginLeft: "20px",
                  flex: 1,
                }}
              >
                <p className="text-[#5E656B]">{t("INTEREST_PAYMENT_METHOD")}</p>
                <p className="text-[#131618] text-[16px] font-bold">
                  {t("ON_MATURITY_DATE")}
                </p>
              </Box>
            </Box>
            <Box
              style={{
                display: "flex",
              }}
            >
              <Box
                style={{
                  borderRight: "1px dashed #D8DDE2",
                  marginLeft: "20px",
                  flex: 1,
                }}
              >
                <p className="text-[#5E656B]">{t("RENEWAL_METHOD")}</p>
                <p className="text-[#131618] text-[16px] font-bold">
                  {t("PRINCIPAL_AND_INTEREST")}
                </p>
              </Box>
              <Box
                style={{
                  marginLeft: "20px",
                  flex: 1,
                }}
              >
                <p className="text-[#5E656B]">{t("MATURITY_DATE")}</p>
                <p className="text-[#131618] text-[16px] font-bold">
                  22/12/2023
                </p>
              </Box>
            </Box>
          </Box>
        </Box>
      ),
    },
    {
      value: "interest details",
      label: t("INTEREST_DETAILS"),
      panelContent: (
        <Box
          style={{
            padding: "20px",
          }}
        >
          <Box
            style={{
              display: "flex",
              justifyContent: "start",
            }}
          >
            <Box
              style={{
                flex: 1,
                borderRight: "1px dashed #D8DDE2",
              }}
            >
              <p className="text-[#5E656B]">{t("INTEREST_RATE")}</p>
              <p className="text-[#131618] text-[16px] font-bold">5%</p>
            </Box>
            <Box
              style={{
                flex: 1,
                borderRight: "1px dashed #D8DDE2",
                marginLeft: "20px",
              }}
            >
              <p className="text-[#5E656B]">{t("LAST_PAID_INTEREST")}</p>
              <p className="text-[#131618] text-[16px] font-bold">
                {toCurrency(220000)} VND
              </p>
            </Box>
            <Box
              style={{
                flex: 1,
                borderRight: "1px dashed #D8DDE2",
                marginLeft: "20px",
              }}
            >
              <p className="text-[#5E656B]">{t("ACCRUED_INTEREST")}</p>
              <p className="text-[#131618] text-[16px] font-bold">
                {toCurrency(1600000)} VND
              </p>
            </Box>
            <Box
              style={{
                flex: 1,
                marginLeft: "20px",
              }}
            >
              <p className="text-[#5E656B]">{t("PERIODIC_INTEREST")}</p>
              <p className="text-[#131618] text-[16px] font-bold">
                {toCurrency(220000)} VND
              </p>
            </Box>
          </Box>
        </Box>
      ),
    },
    {
      value: "account info",
      label: t("ACCOUNT_INFO"),
      panelContent: (
        <Box
          style={{
            padding: "20px",
          }}
        >
          <Box
            style={{
              display: "flex",
              justifyContent: "space-between",
              width: "100%",
            }}
          >
            {[
              {
                label: t("ACCOUNT_NUMBER"),
                value: "32432324324",
                flexGrow: 1,
              },
              {
                label: t("ACCOUNT_NAME"),
                value: t("BUSINESS_GROWTH_SAVINGS"),
                flexGrow: 1,
              },
              {
                label: t("OPENING_BRANCH"),
                value: "CN QUAN 2",
                flexGrow: 1,
              },
              {
                label: t("ACCOUNT_TYPE"),
                value: t("TERM_DEPOSIT"),
                flexGrow: 1,
              },
            ].map((item, index, array) => (
              <Box
                style={{
                  flexGrow: item.flexGrow,
                  textAlign: "start",
                  borderRight:
                    index !== array.length - 1 ? "1px dashed #D8DDE2" : "none",
                  padding: "0 10px",
                  flexBasis: 0,
                }}
              >
                <p className="text-[#5E656B]">{item.label}</p>
                <p className="text-[#131618] text-[16px] font-bold">
                  {item.value}
                </p>
              </Box>
            ))}
          </Box>
          <Box
            style={{
              display: "flex",
              justifyContent: "space-between",
              width: "100%",
              marginTop: "20px",
            }}
          >
            {[
              {
                label: t("PRODUCT_NAME"),
                value: t("SAVING_ACCOUNT"),
                flexGrow: 1,
              },
              {
                label: t("OPEN_DATE"),
                value: "23/02/2023",
                flexGrow: 1,
              },
              {
                label: t("MATURITY_DATE"),
                value: "23/02/2028",
                flexGrow: 1,
              },
              {
                label: t("OPENING_CHANNEL"),
                value: t("OFFLINE"),
                flexGrow: 1,
              },
            ].map((item, index, array) => (
              <Box
                style={{
                  flexGrow: item.flexGrow,
                  textAlign: "start",
                  borderRight:
                    index !== array.length - 1 ? "1px dashed #D8DDE2" : "none",
                  padding: "0 10px",
                  flexBasis: 0,
                }}
              >
                <p className="text-[#5E656B]">{item.label}</p>
                <p className="text-[#131618] text-[16px] font-bold">
                  {item.value}
                </p>
              </Box>
            ))}
          </Box>
          <Box
            style={{
              display: "flex",
              justifyContent: "space-between",
              width: "100%",
              marginTop: "20px",
            }}
          >
            {[
              {
                label: t("CURRENCY"),
                value: "VND",
                flexGrow: 1,
              },
            ].map((item, index, array) => (
              <Box
                style={{
                  flexGrow: item.flexGrow,
                  textAlign: "start",
                  borderRight:
                    index !== array.length - 1 ? "1px dashed #D8DDE2" : "none",
                  padding: "0 10px",
                  flexBasis: 0,
                }}
              >
                <p className="text-[#5E656B]">{item.label}</p>
                <p className="text-[#131618] text-[16px] font-bold">
                  {item.value}
                </p>
              </Box>
            ))}
          </Box>
        </Box>
      ),
    },
  ];

  const tabsHistory = [
    {
      value: "transaction history",
      label: t("TRANSACTION_HISTORY"),
      panelContent: (
        <Box>
          <TableComponent
            dataSource={{
              columns: [
                { title: t("TRANSACTION_ID") },
                { title: t("TRANSACTION_DATE") },
                { title: t("TRANSACTION_REMARKS"), size: 200 },
                { title: <Text ta="right">{t("DEBIT_AMOUNT")}</Text> },
                { title: <Text ta="right">{t("CREDIT_AMOUNT")}</Text> },
              ],
              data: pagedAccountHistory.map((item: any) => {
                const hasDebit = !!item.debitAmount;
                const hasCredit = !!item.creditAmount;
                return {
                  transactionId: <Text weight={600}>{item.transactionId}</Text>,
                  transactionDate: formatTransactionDate(item.transactionDate),
                  transactionRemarks: item.transactionRemarks,
                  debitAmount: hasDebit ? (
                    <Text c="#E74C3C" fw={600} ta="right">
                      {`-${toCurrency(item.debitAmount)} VND`}
                    </Text>
                  ) : (
                    <Text ta="right">-</Text>
                  ),
                  creditAmount: hasCredit ? (
                    <Text c="#34AA44" fw={600} ta="right">
                      {`+${toCurrency(item.creditAmount)} VND`}
                    </Text>
                  ) : (
                    <Text ta="right">-</Text>
                  ),
                };
              }),
            }}
          />
          <Space h={rem(10)} />
          <Group position="apart" sx={{ padding: "12px 20px" }}>
            <PaginationInfo current={historyPage} total={historyTotalPages} />
            <PaginationControls
              currentPage={historyPage}
              totalPages={historyTotalPages}
              onPageChange={setHistoryPage}
            />
          </Group>
        </Box>
      ),
    },
    {
      value: "view next installment",
      label: t("RENEWAL_HISTORY"),
      panelContent: (
        <Box>
          <TableComponent
            dataSource={{
              columns: [
                { title: t("PRINCIPAL_AMOUNT"), size: 160 },
                { title: t("INTEREST_RATE"), size: 200 },
                { title: t("INTEREST_AMOUNT") },
                { title: t("VALUE_DATE") },
                { title: t("MATURITY_DATE") },
              ],
              data: pagedRenewalHistory.map((item: any) => {
                return {
                  principalAmount: <Text fw={600}>{item.principalAmount}</Text>,
                  interestRate: (
                    <Text>
                      {item.interestRate} {t("YEAR")}
                    </Text>
                  ),
                  interestAmount: <Text fw={600}>{item.interestAmount}</Text>,
                  valueDate: formatTransactionDate(item.valueDate),
                  maturityDate: formatTransactionDate(item.maturityDate),
                };
              }),
            }}
            openedRow={openedRow}
          />

          <Space h={rem(10)} />
          <Group position="apart" sx={{ padding: "12px 20px" }}>
            <PaginationInfo current={renewalPage} total={renewalTotalPages} />
            <PaginationControls
              currentPage={renewalPage}
              totalPages={renewalTotalPages}
              onPageChange={setRenewalPage}
            />
          </Group>
        </Box>
      ),
    },
  ];

  const downloadFile = (url: string) => {
    const link = document.createElement("a");
    link.href = url;
    link.download = url.split("/").pop() || "file.pdf";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <div>
      <Text weight={800} size={24}>
        {t("ACCOUNT_DETAILS")}
      </Text>
      <Space h={rem(10)} />
      <AccountCard
        icon={IconAccountImg}
        title="32432324324"
        description={`TKTT-VND-${t("SAVING_ACCOUNT_1")}`}
      />
      <Box
        style={{
          display: "flex",
          gap: "10px",
        }}
      >
        <Box
          style={{
            flex: "1",
          }}
        >
          <TabsCustomer defaultValue="account summary" tabs={depositData} />
          <TabsHistory
            accountId={accountId}
            defaultValue="transaction history"
            tabs={tabsHistory}
          />
        </Box>
        <Box
          style={{
            width: "309px",
            height: "fit-content",
            borderRadius: "10px",
            boxShadow: "0px 2px 8px 0px #638DBF1A",
            background: "#FDFDFD",
            margin: "24px 0px",
          }}
        >
          <Box
            style={{
              fontWeight: 700,
              fontSize: "18px",
              lineHeight: "25.2px",
              letterSpacing: "0%",
              borderBottom: "2px solid #dee2e6",
              padding: "18.5px",
            }}
          >
            {t("QUICK_ACTION")}
          </Box>
          {actions.map(({ icon, action, label }) => (
            <Box
              key={label}
              sx={{
                padding: "11px",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                cursor: "pointer",
              }}
              onClick={action}
            >
              <Box sx={{ display: "flex", gap: "10px", alignItems: "center" }}>
                {icon}
                <Box>{label}</Box>
              </Box>
              <IconArrowRight stroke={2} color="#2A81D0" />
            </Box>
          ))}
        </Box>
      </Box>
      <ContractInformation
        opened={isOpenContractInformation}
        onClose={closeContractInformation}
        fileUrl="/pdf/file.pdf"
        onDowload={() => {
          downloadFile("/pdf/file.pdf");
        }}
      />
    </div>
  );
};

const TabsCustomer = ({ defaultValue, tabs }: any) => {
  return (
    <Tabs
      styles={{
        root: {
          background: "#FDFDFD",
          borderRadius: "8px",
          boxShadow: "0px 2px 8px 0px #638DBF1A",
          margin: "24px 0px",
        },
      }}
      defaultValue={defaultValue}
    >
      <Tabs.List sx={{ borderBottom: "none" }}>
        {tabs.map((item: any) => {
          return (
            <Tabs.Tab
              sx={() => ({
                border: "none",
                "&[data-active]": {
                  color: "#2A81D0",
                  fontWeight: 700,
                  border: "none",
                },
                "&[data-active]:hover": {
                  border: "none",
                },
                height: "64px",
              })}
              value={item.value}
            >
              {item.label}
            </Tabs.Tab>
          );
        })}
      </Tabs.List>

      {tabs.map((item: any) => {
        return <Tabs.Panel value={item.value}>{item.panelContent}</Tabs.Panel>;
      })}
    </Tabs>
  );
};

const TabsHistory = ({ defaultValue, tabs, accountId }: any) => {
  const { t } = useTranslation();
  const [opened, setOpened] = useState(false);
  const [activeTab, setActiveTab] = useState(defaultValue);

  const today = new Date();
  const maxDays = 180;

  const addDays = (date: Date, days: number): Date => {
    const result = new Date(date);
    result.setDate(result.getDate() + days);
    return result;
  };

  const form = useForm<{
    fromDate: Date | null;
    toDate: Date;
  }>({
    initialValues: {
      fromDate: null,
      toDate: today,
    },
  });

  const handleFromDateChange = (value: Date | null) => {
    const { toDate } = form.values;
    if (!value) return;

    if (value > toDate) {
      showNotification({
        title: t("ERROR"),
        message:
          t("FROM_DATE_CANNOT_BE_AFTER_TO_DATE") ||
          `${t("START_DATE_AFTER_END_DATE")}`,
        color: "red",
        icon: <IconAlertTriangle size={18} />,
      });
      return;
    }

    form.setFieldValue("fromDate", value);
  };

  const handleToDateChange = (value: Date | null) => {
    if (!value) return;

    form.setFieldValue("toDate", value);

    const { fromDate } = form.values;
    const minFromDate = addDays(value, -maxDays);
    if (fromDate && (fromDate > value || fromDate < minFromDate)) {
      form.setFieldValue("fromDate", null);
    }
  };

  const handleRefreshForm = () => {
    form.reset();
  };
  return (
    <>
      <Tabs
        styles={{
          root: {
            background: "#FDFDFD",
            borderRadius: "8px",
            boxShadow: "0px 2px 8px 0px #638DBF1A",
            margin: "24px 0px",
          },
        }}
        defaultValue={defaultValue}
        value={activeTab}
        onTabChange={setActiveTab}
      >
        <Tabs.List
          sx={() => ({
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            borderBottom: "none",
          })}
        >
          {tabs.map((item: any) => (
            <Tabs.Tab
              sx={() => ({
                border: "none",
                "&[data-active]": {
                  color: "#2A81D0",
                  fontWeight: 700,
                  border: "none",
                },
                "&[data-active]:hover": {
                  border: "none",
                },
                height: "64px",
              })}
              value={item.value}
            >
              {item.label}
            </Tabs.Tab>
          ))}
          <Box
            sx={(theme) => ({
              marginLeft: "auto",
              marginRight: "20px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            })}
          >
            <Button
              variant="subtle"
              leftIcon={<IconDownload size="0.9rem" />}
              color="#2A81D0"
            >
              {t("DOWNLOAD")}
            </Button>
            <Button
              variant="subtle"
              leftIcon={<IconPrinter size="0.9rem" />}
              color="#2A81D0"
            >
              {t("PRINT")}
            </Button>
            <Box
              style={{
                padding: "4px 6px",
                borderRadius: "8px",
                background: "#E5F4FE",
                display: "flex",
                alignItems: "center",
                marginLeft: "8px",
              }}
              onClick={() => setOpened(true)}
            >
              <IconSearch color="#2A81D0" />
            </Box>
          </Box>
        </Tabs.List>

        {tabs.map((item: any) => {
          return (
            <Tabs.Panel value={item.value}>{item.panelContent}</Tabs.Panel>
          );
        })}
      </Tabs>

      <Modal
        opened={opened}
        onClose={() => setOpened(false)}
        withCloseButton
        fullScreen
        overlayProps={{ opacity: 0.5, blur: 0.5 }}
        transitionProps={{ transition: "slide-right", duration: 300 }}
        styles={{
          header: { borderBottom: "solid 1px #dee2e6" },
          title: {
            fontWeight: 700,
            fontSize: "18px",
            color: "#131618",
          },
          content: {
            width: "594px",
            position: "fixed",
            top: 0,
            right: 0,
            borderRadius: "0 !important",
            display: "flex",
            flexDirection: "column",
            height: "100%",
          },
          body: {
            flex: 1,
            overflowY: "auto",
            paddingBottom: "80px",
            paddingTop: "12px !important",
          },
        }}
        title={`${t("TRANSACTION_HISTORY_FILTER")}`}
      >
        <form className="h-full" onSubmit={() => {}}>
          <TextInput
            label={`${t("SOURCE_ACCOUNT")}`}
            placeholder="VND-LD100632"
            mb="sm"
            name="search"
            disabled
          />
          <Box
            style={{
              display: "flex",
              justifyContent: "space-between",
              gap: "10px",
            }}
            mb="sm"
          >
            <DateInput
              label={t("TRANSACTION_FROM_DATE")}
              valueFormat="DD/MM/YYYY"
              style={{ flex: 1 }}
              minDate={addDays(form.values.toDate, -maxDays)}
              maxDate={form.values.toDate}
              {...form.getInputProps("fromDate")}
              onChange={handleFromDateChange}
              onKeyDown={(e) => e.preventDefault()}
              onPaste={(e) => e.preventDefault()}
            />

            <DateInput
              label={t("TRANSACTION_TO_DATE")}
              placeholder={t("FIND_ACCOUNT") as string}
              valueFormat="DD/MM/YYYY"
              // readOnly
              style={{ flex: 1 }}
              // maxDate={today}
              {...form.getInputProps("toDate")}
              onChange={handleToDateChange}
            />
          </Box>
          {activeTab === "transaction history" &&
            accountId !== "32432324324" && (
              <Select
                label={t("AMOUNT_TYPE")}
                placeholder={`${t("SELECT_CURRENCY_TYPE")}`}
                data={[
                  {
                    value: "Debit amount",
                    label: t("DEBIT_AMOUNT") || "Debit amount",
                  },
                  {
                    value: "Credit amount",
                    label: t("CREDIT_AMOUNT") || "Credit amount",
                  },
                  { value: "All", label: t("ALL") || "All" },
                ]}
                defaultValue={t("ALL") || "All"}
                mb="sm"
                name="accountCurrency"
                rightSection={<IconArrowsUpDown size="1rem" />}
              />
            )}
          {activeTab === "transaction history" &&
            accountId === "32432324324" && (
              <Box>
                <Select
                  label={t("AMOUNT_TYPE")}
                  placeholder={`${t("SELECT_CURRENCY_TYPE")}`}
                  data={[
                    {
                      value: "Debit amount",
                      label: t("DEBIT_AMOUNT") || "Debit amount",
                    },
                    {
                      value: "Credit amount",
                      label: t("CREDIT_AMOUNT") || "Credit amount",
                    },
                    { value: "All", label: t("ALL") || "All" },
                  ]}
                  defaultValue={t("ALL") || "All"}
                  mb="sm"
                  name="accountCurrency"
                  rightSection={<IconArrowsUpDown size="1rem" />}
                />
                <Select
                  label={t("TRANSACTION_CHANNEL")}
                  placeholder={`${t("SELECT_TRANSACTION_CHANNEL")}`}
                  data={[
                    { value: "ib", label: t("IB") || "ib" },
                    { value: "mb", label: t("MB") || "mb" },
                    { value: "branch", label: t("BRANCH") || "Branch" },
                    { value: "others", label: t("OTHERS") || "Others" },
                    { value: "All", label: t("ALL") || "All" },
                  ]}
                  defaultValue={t("ALL") || "All"}
                  mb="sm"
                  name="accountType"
                  rightSection={<IconChevronDown size="1rem" />}
                />
                <Select
                  label={t("TRANSACTION_TYPE")}
                  placeholder={`${t("SELECT_ACCOUNT_TYPE")}`}
                  data={[
                    { value: "ft", label: "FT" },
                    { value: "tt", label: "TT" },
                    { value: "bill", label: t("BILL") || "Bill" },
                    { value: "atm", label: "ATM" },
                    { value: "All", label: t("ALL") || "All" },
                  ]}
                  defaultValue={t("ALL") || "All"}
                  mb="sm"
                  name="accountType"
                  rightSection={<IconChevronDown size="1rem" />}
                />
              </Box>
            )}
          <Box
            style={{
              position: "absolute",
              bottom: 0,
              left: 0,
              width: "100%",
              borderTop: "1px solid #e0e0e0",
              padding: "10px 16px",
              display: "flex",
              justifyContent: "flex-end",
              gap: "10px",
            }}
          >
            <Button
              sx={() => ({
                border: "solid 1px #2A81D0",
                borderRadius: "8px",
                padding: "8px",
              })}
              variant="outline"
            >
              <IconRefresh
                size={18}
                color="#2A81D0"
                onClick={handleRefreshForm}
              />
            </Button>
            <Button
              type="submit"
              sx={() => ({
                backgroundColor: "#2A81D0",
                color: "white",
                borderRadius: "8px",
                padding: "8px 10px",
                cursor: "pointer",
                fontWeight: 400,
              })}
            >
              {t("SEARCH")}
            </Button>
          </Box>
        </form>
      </Modal>
    </>
  );
};

export default DepositAccountPanelDetail;
